import ApiCaller from '../constants/ApiCaller'
import urls from '../constants/apiurl'

/**
 * @typedef {Object} PaymentsSummary
 * @property {number | null} totalUsersCashConvertableChips
 * @property {number | null} totalPayableAmountToUsers
 * @property {number | null} totalPaidChipsToUsers
 * @property {number | null} totalAmountPaidToUsers
 * @property {number | null} bankPaidTotal
 * @property {number | null} bankRejectedTotal
 * @property {number | null} bankProcessingTotal
 * @property {number | null} bankPendingTotal
 */

const PAYMENT_KEY_ALIASES = {
  totalUsersCashConvertableChips: [
    'totalCashConvertibleChips',
    'total_cash_convertible_chips',
    'totalUsersCashConvertableChips',
    'totalUsersCashConvertibleChips',
    'total_users_cash_convertable_chips',
    'total_users_cash_convertible_chips'
  ],
  totalPayableAmountToUsers: [
    'totalAmountPayableToUsers',
    'total_amount_payable_to_users',
    'totalPayableAmountToUsers',
    'total_payable_amount_to_users'
  ],
  totalPaidChipsToUsers: [
    'totalChipsPaidToUsers',
    'total_chips_paid_to_users',
    'totalPaidChipsToUsers',
    'total_paid_chips_to_users'
  ],
  totalAmountPaidToUsers: ['totalAmountPaidToUsers', 'total_amount_paid_to_users'],
  bankPaidTotal: [
    'totalAmountPaidByBank',
    'total_amount_paid_by_bank',
    'bankPaidTotal',
    'bank_paid_total'
  ],
  bankRejectedTotal: [
    'totalAmountRejectedByBank',
    'total_amount_rejected_by_bank',
    'bankRejectedTotal',
    'bank_rejected_total'
  ],
  bankProcessingTotal: [
    'totalAmountProcessingByBank',
    'total_amount_processing_by_bank',
    'bankProcessingTotal',
    'bank_processing_total'
  ],
  bankPendingTotal: [
    'totalAmountPendingAtBank',
    'total_amount_pending_at_bank',
    'bankPendingTotal',
    'bank_pending_total'
  ]
}

const toNumberOrNull = value => {
  if (value === undefined || value === null || value === '') return null
  if (typeof value === 'number') return Number.isNaN(value) ? null : value
  const parsed = Number(String(value).replace(/,/g, '').trim())
  return Number.isNaN(parsed) ? null : parsed
}

const getByPath = (obj, path) =>
  path.split('.').reduce((acc, key) => (acc && acc[key] != null ? acc[key] : undefined), obj)

const candidatePayloads = responseJson => [
  responseJson?.data,
  responseJson?.data?.data,
  responseJson?.data?.summary,
  responseJson?.summary,
  responseJson?.result,
  responseJson
].filter(Boolean)

const pickMetricValue = (source, aliases) => {
  for (let i = 0; i < aliases.length; i += 1) {
    const direct = source?.[aliases[i]]
    if (direct !== undefined && direct !== null && direct !== '') return direct
    const nested = getByPath(source, aliases[i])
    if (nested !== undefined && nested !== null && nested !== '') return nested
  }
  return null
}

/**
 * @param {unknown} responseJson
 * @returns {PaymentsSummary}
 */
export const mapPaymentsSummary = responseJson => {
  const payloads = candidatePayloads(responseJson)
  const source = payloads[0] || {}

  return {
    totalUsersCashConvertableChips: toNumberOrNull(
      pickMetricValue(source, PAYMENT_KEY_ALIASES.totalUsersCashConvertableChips)
    ),
    totalPayableAmountToUsers: toNumberOrNull(
      pickMetricValue(source, PAYMENT_KEY_ALIASES.totalPayableAmountToUsers)
    ),
    totalPaidChipsToUsers: toNumberOrNull(
      pickMetricValue(source, PAYMENT_KEY_ALIASES.totalPaidChipsToUsers)
    ),
    totalAmountPaidToUsers: toNumberOrNull(
      pickMetricValue(source, PAYMENT_KEY_ALIASES.totalAmountPaidToUsers)
    ),
    bankPaidTotal: toNumberOrNull(pickMetricValue(source, PAYMENT_KEY_ALIASES.bankPaidTotal)),
    bankRejectedTotal: toNumberOrNull(
      pickMetricValue(source, PAYMENT_KEY_ALIASES.bankRejectedTotal)
    ),
    bankProcessingTotal: toNumberOrNull(
      pickMetricValue(source, PAYMENT_KEY_ALIASES.bankProcessingTotal)
    ),
    bankPendingTotal: toNumberOrNull(
      pickMetricValue(source, PAYMENT_KEY_ALIASES.bankPendingTotal)
    )
  }
}

/**
 * @param {string} accessToken
 * @returns {Promise<PaymentsSummary>}
 */
export const fetchPaymentsSummary = async accessToken => {
  const res = await ApiCaller.get({}, urls.paymentsSummary, accessToken)
  const response = await res.response.json()

  const isSuccess = response?.status === 200 || response?.status === '200'
  if (!isSuccess) {
    throw new Error(response?.message || 'Failed to fetch payments summary')
  }

  return mapPaymentsSummary(response)
}
